import React, { useCallback } from 'react'

import { useEffect, useClickOutside, useMemo, useMediaQueries } from 'hooks'

import { formatDateToBackend } from 'helpers/date.js'

import AllCenter from 'components/_old/AllCenter/AllCenter.jsx'
import Button from 'components/_old/Button/Button.jsx'
import Link from 'components/_old/Link/Link.jsx'

import { Card } from 'components/atoms/Card'
import { DateInput } from 'components/atoms/DateInput'
import { MobileLayout, MobileLayoutFooterButton } from 'components/atoms/Layouts'
import { NavigationBar } from 'components/atoms/NavigationBar'
import { Paper } from 'components/atoms/Paper'

import { usePeriodWithCustomRange } from 'app/pages/Securities/hooks/usePeriodWithCustomRange'
import { useCustomRange } from 'app/pages/Securities/Security/hooks/useCustomRange'

import ColumnarLayout, { Column } from '../ColumnarLayout/ColumnarLayout.jsx'
import { Modal } from '../Modal/Modal'

import './CustomRangePicker.css'

type CustomRangePickerProps = {
  onClose: () => void
}

const DAY_DURATION = 86400000

const CustomRangePicker = ({ onClose }: CustomRangePickerProps): JSX.Element => {
  const { desktop } = useMediaQueries()
  const { inputValue, setInputValue, setChartValue, lastAppliedInputValue, setLastAppliedInputValue } = useCustomRange()
  const { resetPreviousPeriod } = usePeriodWithCustomRange()

  const [rootRef, hasClickedOutside] = useClickOutside<HTMLDivElement>()

  useEffect(() => {
    if (desktop && hasClickedOutside) {
      setInputValue(lastAppliedInputValue)
      onClose()
    }

    // eslint-disable-next-line
  }, [hasClickedOutside, onClose, desktop])

  const handleFromDateChange = useCallback(
    (event): void => {
      setInputValue({
        ...inputValue,
        from: event.target.value,
      })

      resetPreviousPeriod()
    },
    [inputValue, resetPreviousPeriod, setInputValue],
  )

  const handleToDateChange = useCallback(
    (event): void => {
      setInputValue({
        ...inputValue,
        to: event.target.value,
      })

      resetPreviousPeriod()
    },
    [inputValue, resetPreviousPeriod, setInputValue],
  )

  const handleClearClick = useCallback((): void => {
    setInputValue({ from: '', to: '' })
  }, [setInputValue])

  const handleApplyClick = useCallback((): void => {
    setChartValue({ to: inputValue.to, from: inputValue.from })
    setLastAppliedInputValue({ to: inputValue.to, from: inputValue.from })
    onClose()
  }, [setChartValue, inputValue.to, inputValue.from, setLastAppliedInputValue, onClose])

  const today = formatDateToBackend(new Date()) as string
  const yesterday = formatDateToBackend(new Date(Date.now() - DAY_DURATION)) as string

  const content = useMemo(
    () => (
      <ColumnarLayout mods={{ padding: desktop ? 'small' : 'nano' }} flexDirection={desktop ? undefined : 'column'}>
        <Column>
          <DateInput label="From" value={inputValue.from} onChange={handleFromDateChange} max={yesterday} />
        </Column>
        <Column>
          <DateInput label="To" value={inputValue.to} onChange={handleToDateChange} max={today} />
        </Column>
      </ColumnarLayout>
    ),
    [desktop, inputValue.from, inputValue.to, handleFromDateChange, yesterday, handleToDateChange, today],
  )

  const applyButton = useMemo(
    () => (
      <Button mods={{ size: 'big block' }} onClick={handleApplyClick}>
        Apply
      </Button>
    ),
    [handleApplyClick],
  )

  const clearAllButton = useMemo(
    () => (
      <Link onClick={handleClearClick} role="button">
        Clear all
      </Link>
    ),
    [handleClearClick],
  )

  if (!desktop) {
    return (
      <Modal zIndex={1002} open={true} close={null} onClose={onClose}>
        <MobileLayout
          header={
            <NavigationBar onRightPartClick={onClose} rightPartText="Close">
              Custom range
            </NavigationBar>
          }
          content={
            <Paper top={12} bottom={48}>
              {content}
            </Paper>
          }
          footer={
            <Paper top={24}>
              <MobileLayoutFooterButton>{applyButton}</MobileLayoutFooterButton>
              <MobileLayoutFooterButton>
                <AllCenter>{clearAllButton}</AllCenter>
              </MobileLayoutFooterButton>
            </Paper>
          }
        />
      </Modal>
    )
  }

  return (
    <Card className="CustomRangePicker" color="background-default" ref={rootRef}>
      <Paper left={32} right={32} top={80} bottom={24}>
        <Link mods={{ color: 'black' }} onClick={onClose} role="button" className="CustomRangePicker-CloseButton">
          <Paper top={12} bottom={12} right={24} left={24}>
            Cancel
          </Paper>
        </Link>

        {content}
        <Paper top={24}>{applyButton}</Paper>
        <Paper top={24}>
          <AllCenter>{clearAllButton}</AllCenter>
        </Paper>
      </Paper>
    </Card>
  )
}

export { CustomRangePicker }
